import { useCallback, useEffect, useState } from 'react'
import Select from './Select'
import { useLoadPickups } from '@/hooks/PickupHook'
import { Loading, LoadingBlock } from '@/components/BookingAvailablityNew.styles'
import { BookingPickup, PickupLocation } from '@big-red-group/storefront-common-checkout'
import { logger } from '@/logging/Logger'
import { getMetadataItem } from '@/helpers/PickupHelper'
import { TrackBookingFieldEnteredInput } from '@/types'

type RequiredPickupProps = {
  sessionId: number
  handlerKey?: string
  availabilityId?: string
  onChange: (pickup: BookingPickup) => void
  previouslySelectedPickupLocation?: BookingPickup
  bookingFieldEntered?: (bookingField: TrackBookingFieldEnteredInput) => void
}

const log = logger()

const RequiredPickup: React.FC<RequiredPickupProps> = ({
  onChange,
  handlerKey,
  sessionId,
  availabilityId,
  previouslySelectedPickupLocation,
  bookingFieldEntered,
}) => {
  // text displayed in select drop down
  const [pickupLabels, setPickupLabels] = useState<{ label: string; value: string }[]>([])

  // index corresponding the selected item
  const [pickupIndex, setPickupIndex] = useState<string>('')

  // a map of pickup index (dropdown value) x pickup
  const [pickupObjects, setPickupObjects] = useState<Map<number, PickupLocation>>(new Map())

  // API error
  const [errorText, setErrorText] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const loadPickups = useLoadPickups()

  const getInfo = (pickup: PickupLocation): string => {
    let { info, address, hour, minute } = pickup
    info = address!
    // TODO add logic to add am/pm
    if (hour) {
      info = info?.concat(' @ ', hour, '.', minute ?? '00')
    }
    return info
  }

  const getTremsInfo = (pickup: PickupLocation): string => {
    let { info, generalLocation, value } = pickup
    info = generalLocation!.concat('-', value!)
    return info
  }

  const processPickups = useCallback(
    (handlerKey: string | undefined, pickups: PickupLocation[]) => {
      const newPickUpLabels: { label: string; value: string }[] = previouslySelectedPickupLocation
        ? [{ label: previouslySelectedPickupLocation.address!, value: '' }]
        : [{ label: 'Select a pickup location', value: '' }]

      const newPickUpObjects: Map<number, PickupLocation> = new Map()

      pickups.forEach((pickup, index) => {
        let { info } = pickup
        info =
          handlerKey && handlerKey === 'trems2'
            ? getTremsInfo(pickup as PickupLocation)
            : getInfo(pickup as PickupLocation)

        // add the options for new drop down select
        const previousInfo = previouslySelectedPickupLocation
          ? getMetadataItem(previouslySelectedPickupLocation, 'info')
          : undefined
        if (info !== previousInfo) {
          newPickUpLabels.push({ label: info, value: index.toString() })
        }
        // adding to new map to return the pickup object on selection
        newPickUpObjects.set(index, { ...pickup, info })
      })

      setPickupLabels(newPickUpLabels)
      setPickupObjects(newPickUpObjects)

      setIsLoading(false)
    },
    [previouslySelectedPickupLocation]
  )

  const getPickups = useCallback(() => {
    loadPickups(sessionId, availabilityId)
      .then(({ data: pickupPoints, error }) => {
        if (error) {
          setErrorText(true)
          setIsLoading(false)
          return
        }

        processPickups(handlerKey, pickupPoints)
      })
      .catch((error) => {
        log.error('Error loading pickups', {}, error as Error)
        setErrorText(true)
        setIsLoading(false)
      })
  }, [availabilityId, handlerKey, loadPickups, processPickups, sessionId])

  useEffect(() => {
    getPickups()
  }, [])

  // useEffect(() => {
  //   if (previouslySelectedPickupLocation) {
  //     onChange({ pickup: previouslySelectedPickupLocation! })
  //   }
  // }, [previouslySelectedPickupLocation, onChange])

  if (errorText) {
    return <div>Unable to load pickup locations now. Please try again later.</div>
  }

  const handleOnChange = (index: string): void => {
    setPickupIndex(index)
    const pickupLocation = pickupObjects?.get(parseInt(index))
    if (!pickupLocation) {
      return
    }
    const pickup: BookingPickup = {
      id: pickupLocation.id ?? null,
      address: pickupLocation.address ?? pickupLocation.value ?? '',
      type: 'PRESET',
      metadata: Object.entries(pickupLocation)
        .filter(
          ([key]) =>
            key !== 'id' &&
            key !== 'address' &&
            key !== 'type' &&
            pickupLocation[key as keyof PickupLocation] !== undefined
        )
        .map(([key, value]) => ({ key, value })),
    }
    onChange(pickup)
    bookingFieldEntered?.({
      label: 'BookingPickupRequired',
      value: pickup.address,
      section: 'Pick up location',
      fieldTitle: 'Choose pickup location',
      fieldType: 'select',
    })
  }

  return (
    <div data-testid="pickup-modal">
      {pickupLabels.length === 0 || isLoading ? (
        <LoadingBlock>
          <Loading />
        </LoadingBlock>
      ) : (
        <Select name="pickup" options={pickupLabels} onChange={handleOnChange} value={pickupIndex} />
      )}
    </div>
  )
}

export default RequiredPickup
